import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical, Trash2, Plus, ChevronDown } from "lucide-react";
import { BLOCK_TYPES, defaultProps } from "../../lib/blocks";
import { Btn, Card, Input, Localized } from "./Fields";
import ImageUpload from "./ImageUpload";

const BlockEditor = ({ block, onChange }) => {
  const p = block.props || {};
  const set = (k, v) => onChange({ ...block, props: { ...p, [k]: v } });

  switch (block.type) {
    case "hero":
      return (
        <div className="space-y-4">
          <Localized label="Kicker" value={p.eyebrow} onChange={(v) => set("eyebrow", v)} />
          <Localized label="Başlık" value={p.title} onChange={(v) => set("title", v)} />
          <Localized label="Alt Başlık" value={p.subtitle} onChange={(v) => set("subtitle", v)} textarea />
          <ImageUpload label="Arka Plan Görseli" value={p.image} onChange={(v) => set("image", v)} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Localized label="CTA Metni" value={p.cta_label} onChange={(v) => set("cta_label", v)} />
            <Input label="CTA URL" value={p.cta_url} onChange={(v) => set("cta_url", v)} />
          </div>
        </div>
      );
    case "text":
      return (
        <div className="space-y-4">
          <Localized label="Başlık" value={p.heading} onChange={(v) => set("heading", v)} />
          <Localized label="Metin" value={p.body} onChange={(v) => set("body", v)} textarea />
        </div>
      );
    case "two-column":
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Localized label="Sol Sütun" value={p.left} onChange={(v) => set("left", v)} textarea />
          <Localized label="Sağ Sütun" value={p.right} onChange={(v) => set("right", v)} textarea />
        </div>
      );
    case "image":
      return (
        <div className="space-y-4">
          <ImageUpload label="Görsel" value={p.image} onChange={(v) => set("image", v)} />
          <Localized label="Alt yazı" value={p.caption} onChange={(v) => set("caption", v)} />
        </div>
      );
    case "gallery": {
      const items = p.images || [];
      const upd = (arr) => set("images", arr);
      return (
        <div className="space-y-3">
          {items.map((img, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex-1"><ImageUpload label={`Görsel ${i + 1}`} value={img} onChange={(v) => upd(items.map((x, j) => (j === i ? v : x)))} /></div>
              <Btn variant="danger" onClick={() => upd(items.filter((_, j) => j !== i))}><Trash2 size={12} /></Btn>
            </div>
          ))}
          <Btn variant="ghost" onClick={() => upd([...items, ""])}><Plus size={12} /> Görsel Ekle</Btn>
        </div>
      );
    }
    case "stats": {
      const items = p.items || [];
      const upd = (arr) => set("items", arr);
      return (
        <div className="space-y-3">
          {items.map((s, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <Input label={`Değer #${i + 1}`} value={s.value} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, value: v } : x)))} />
              <div className="md:col-span-2"><Localized label="Etiket" value={s.label} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, label: v } : x)))} /></div>
              <Btn variant="danger" onClick={() => upd(items.filter((_, j) => j !== i))}><Trash2 size={12} /></Btn>
            </div>
          ))}
          <Btn variant="ghost" onClick={() => upd([...items, { value: "", label: { tr: "", en: "", ar: "" } }])}><Plus size={12} /> İstatistik Ekle</Btn>
        </div>
      );
    }
    case "cta":
      return (
        <div className="space-y-4">
          <Localized label="Başlık" value={p.title} onChange={(v) => set("title", v)} />
          <Localized label="Metin" value={p.body} onChange={(v) => set("body", v)} textarea />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Localized label="Buton Metni" value={p.button_label} onChange={(v) => set("button_label", v)} />
            <Input label="Buton URL" value={p.button_url} onChange={(v) => set("button_url", v)} />
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={!!p.dark} onChange={(e) => set("dark", e.target.checked)} />
            Koyu arka plan
          </label>
        </div>
      );
    case "cards": {
      const items = p.items || [];
      const upd = (arr) => set("items", arr);
      return (
        <div className="space-y-4">
          {items.map((c, i) => (
            <div key={i} className="border border-black/10 p-4">
              <Localized label="Başlık" value={c.title} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, title: v } : x)))} />
              <div className="mt-4"><Localized label="Metin" value={c.body} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, body: v } : x)))} textarea /></div>
              <div className="mt-4"><ImageUpload label="Görsel" value={c.image} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, image: v } : x)))} /></div>
              <div className="mt-4 text-right"><Btn variant="danger" onClick={() => upd(items.filter((_, j) => j !== i))}><Trash2 size={12} /> Kartı sil</Btn></div>
            </div>
          ))}
          <Btn variant="ghost" onClick={() => upd([...items, { title: { tr: "", en: "", ar: "" }, body: { tr: "", en: "", ar: "" }, image: "" }])}><Plus size={12} /> Kart Ekle</Btn>
        </div>
      );
    }
    case "faq": {
      const items = p.items || [];
      const upd = (arr) => set("items", arr);
      return (
        <div className="space-y-4">
          {items.map((f, i) => (
            <div key={i} className="border border-black/10 p-4">
              <Localized label="Soru" value={f.q} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, q: v } : x)))} />
              <div className="mt-4"><Localized label="Yanıt" value={f.a} onChange={(v) => upd(items.map((x, j) => (j === i ? { ...x, a: v } : x)))} textarea /></div>
              <div className="mt-4 text-right"><Btn variant="danger" onClick={() => upd(items.filter((_, j) => j !== i))}><Trash2 size={12} /></Btn></div>
            </div>
          ))}
          <Btn variant="ghost" onClick={() => upd([...items, { q: { tr: "", en: "", ar: "" }, a: { tr: "", en: "", ar: "" } }])}><Plus size={12} /> SSS Ekle</Btn>
        </div>
      );
    }
    case "html":
      return <textarea rows={10} className="w-full font-mono text-xs bg-white border border-black/15 px-4 py-3" value={p.html || ""} onChange={(e) => set("html", e.target.value)} placeholder="<div>…</div>" />;
    default:
      return <div className="text-brand-medium">Bilinmeyen blok tipi.</div>;
  }
};

const PageBuilder = ({ blocks, onChange }) => {
  const [expandedIdx, setExpandedIdx] = React.useState(null);

  const addBlock = (type) => {
    const nb = { type, order: blocks.length, props: defaultProps(type) };
    onChange([...blocks, nb]);
    setExpandedIdx(blocks.length);
  };
  const updateBlock = (i, next) => onChange(blocks.map((b, j) => (j === i ? next : b)));
  const removeBlock = (i) => onChange(blocks.filter((_, j) => j !== i).map((b, j) => ({ ...b, order: j })));

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = [...blocks];
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    onChange(arr.map((b, j) => ({ ...b, order: j })));
  };

  return (
    <Card title="Sayfa Yapıcı (Page Builder)">
      <div className="mb-6 flex flex-wrap gap-2" data-testid="block-palette">
        {BLOCK_TYPES.map((bt) => (
          <button
            key={bt.type}
            type="button"
            onClick={() => addBlock(bt.type)}
            className="px-3 py-2 border border-black/15 text-xs uppercase tracking-[0.18em] hover:border-brand-navy hover:text-brand-navy transition-colors flex items-center gap-2"
            data-testid={`add-block-${bt.type}`}
          >
            <span className="text-brand-gold">{bt.icon}</span> {bt.label}
          </button>
        ))}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="blocks">
          {(dropProvided) => (
            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} className="space-y-3">
              {(blocks || []).map((b, i) => (
                <Draggable key={i} draggableId={`b-${i}`} index={i}>
                  {(prov) => (
                    <div ref={prov.innerRef} {...prov.draggableProps} className="border border-black/10 bg-white" data-testid={`block-${i}`}>
                      <div className="flex items-center px-4 py-3 border-b border-black/10 bg-brand-light">
                        <button {...prov.dragHandleProps} className="cursor-grab text-brand-medium mr-3" aria-label="Sürükle" data-testid={`block-drag-${i}`}>
                          <GripVertical size={16} />
                        </button>
                        <div className="flex-1 flex items-center gap-3">
                          <span className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-gold">{i + 1}</span>
                          <span className="text-sm font-medium text-brand-navy uppercase tracking-[0.18em]">{(BLOCK_TYPES.find((t) => t.type === b.type) || {}).label || b.type}</span>
                        </div>
                        <button type="button" onClick={() => setExpandedIdx(expandedIdx === i ? null : i)} className="text-brand-medium mr-3" data-testid={`block-toggle-${i}`}>
                          <ChevronDown size={16} className={`transition-transform ${expandedIdx === i ? "rotate-180" : ""}`} />
                        </button>
                        <Btn variant="danger" onClick={() => removeBlock(i)} testId={`block-delete-${i}`}><Trash2 size={12} /></Btn>
                      </div>
                      {expandedIdx === i ? (
                        <div className="p-4 md:p-6">
                          <BlockEditor block={b} onChange={(nb) => updateBlock(i, nb)} />
                        </div>
                      ) : null}
                    </div>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {!(blocks || []).length ? <div className="mt-6 text-brand-medium text-sm">Henüz blok yok. Yukarıdaki paletten blok ekleyin.</div> : null}
    </Card>
  );
};

export default PageBuilder;
